import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Flex,
  Text,
  BigInfoIcon,
  Checkbox,
  Input,
  Button,
  Loader
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumShareRoles } from "@coreapp/constants";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { useSharedFilterController, type SharedFilterDoc } from "./useSharedFilterController";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type SharedFiltersProps = {
  onActiveCountChange?: (count: number) => void;
  onEditSharedFilter?: (filter: SharedFilterDoc) => void;
  onCopyFilter: (definition: any) => void;
  onCopyAndEditFilter?: (definition: any) => void;
  onViewDetails?: (data: any) => void;
  onOpenEdit?: () => void;
};

export const SharedFilters = ({
  onActiveCountChange,
  onEditSharedFilter,
  onCopyFilter,
  onCopyAndEditFilter,
  onViewDetails,
  onOpenEdit
}: SharedFiltersProps) => {
  const { sharedFilters, loading, fetchSharedItems } = useSharedFilterController();
  const { addToast } = useToast() as any;
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [shareModalDoc, setShareModalDoc] = useState<SharedFilterDoc | null>(null);

  useEffect(() => {
    fetchSharedItems("filter");
  }, []);

  // Non-owner filters (what this component manages)
  const nonOwnerFilters = sharedFilters.filter(f => f.userRole !== enumShareRoles.owner);

  // Search and sort
  const filteredSorted = useMemo(() => {
    let list = nonOwnerFilters;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(f => (f.definition?.name || "").toLowerCase().includes(term));
    }
    return [...list].sort((a, b) => {
      const nameA = (a.definition?.name || "").toLowerCase();
      const nameB = (b.definition?.name || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [nonOwnerFilters, searchTerm, sortAsc]);

  // Restore checkbox state from currently active shared filter blocks on the manager
  useEffect(() => {
    if (nonOwnerFilters.length === 0) return;
    const doc = GetDoc();
    const filterMgr = doc?.GetFilterMgr();
    if (!filterMgr) return;

    const contextId = filterMgr.GetContextId();
    const sharedCtxId = `shared_${contextId}`;
    const activeBlocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];
    const activeNames = new Set(activeBlocks.map((b: any) => b.GetName?.()));

    const activeIds = new Set<string>();
    nonOwnerFilters.forEach(f => {
      if (activeNames.has(f.definition?.name)) {
        activeIds.add(f._id);
      }
    });

    if (activeIds.size > 0) {
      setEnabledIds(activeIds);
      onActiveCountChange?.(activeIds.size);
    }
  }, [sharedFilters]);

  useTopic("chartDocumentClosed", () => {
    setEnabledIds(new Set());
  });

  const handleToggle = useCallback(
    (filter: SharedFilterDoc) => {
      const doc = GetDoc();
      const wsMgr = GetWSMgr();
      const filterMgr = doc?.GetFilterMgr();
      if (!filterMgr || !wsMgr) return;

      const contextId = filterMgr.GetContextId();
      const sharedCtxId = `shared_${contextId}`;

      const nextEnabledIds = new Set(enabledIds);
      if (nextEnabledIds.has(filter._id)) {
        nextEnabledIds.delete(filter._id);
      } else {
        nextEnabledIds.add(filter._id);
      }
      setEnabledIds(nextEnabledIds);

      // Preserve owner blocks set by "My filters" tab, rebuild only non-owner blocks
      const existingBlocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];
      const managedNames = new Set(nonOwnerFilters.map(f => f.definition?.name));
      const preservedBlocks = existingBlocks.filter((b: any) => !managedNames.has(b.GetName?.()));

      const newBlocks = nonOwnerFilters
        .filter(f => nextEnabledIds.has(f._id))
        .map(f => {
          const block = new QFilterBlock();
          block.LoadFromJSON({
            ...f.definition,
            contextId: sharedCtxId,
            enabled: true
          });
          return block;
        });

      filterMgr.SetFilterBlocks(sharedCtxId, [...preservedBlocks, ...newBlocks]);
      onActiveCountChange?.(nextEnabledIds.size);

      wsMgr.ApplyChartFilters();
      if (doc && !doc.IsCustomChart()) {
        const scm = wsMgr.GetSubChartMgr();
        const curSubChart = scm.GetCurrentSubChart();
        const topBoxObj = curSubChart.GetTopDBObj();
        const topBoxId = topBoxObj?.GetObjectRecordID();
        scm.PurgeLoadCache();
        doc.DoChartDataQuery(topBoxId);
      }
      ReRenderCanvas();
    },
    [enabledIds, sharedFilters, onActiveCountChange]
  );

  const getRulesFromDefinition = (definition: any) => {
    const block = new QFilterBlock();
    block.LoadFromJSON(definition);
    return block.GetRules?.() ?? (block as any).fFilterRuleList ?? [];
  };

  const getActions = (filter: SharedFilterDoc) => {
    const actions: any[] = [];
    const role = filter.userRole;
    const isCollaborator = role === enumShareRoles.collaborator;
    const isOwner = role === enumShareRoles.owner;

    // Info (all roles)
    actions.push({
      iconName: "circle-info" as const,
      tooltipProps: { message: appstr("Filter details") },
      dataTestId: chartModeTestId("filter", "shared-filter-item-info-btn"),
      onClick: () => {
        const sharedBlock = new QFilterBlock();
        sharedBlock.LoadFromJSON(filter.definition);
        onViewDetails?.({
          name: filter.definition?.name || "",
          ownerDisplayName: filter.owner?.displayName || filter.owner?.username || "",
          isShared: true,
          userRole: filter.userRole,
          rules: getRulesFromDefinition(filter.definition),
          sourceTab: "shared",
          shareProps:
            isOwner || isCollaborator
              ? {
                  definition: filter.definition,
                  filterRef: sharedBlock,
                  sharedDoc: filter,
                  onShareComplete: () => fetchSharedItems("filter")
                }
              : undefined,
          onCopy: () => (onCopyAndEditFilter ?? onCopyFilter)(filter.definition),
          onEdit:
            (isOwner || isCollaborator) && onEditSharedFilter
              ? () => {
                  onOpenEdit?.();
                  onEditSharedFilter(filter);
                }
              : undefined
        });
      }
    });

    // Copy (all roles)
    actions.push({
      iconName: "copy" as const,
      tooltipProps: { message: appstr("Copy to My filters") },
      dataTestId: chartModeTestId("filter", "shared-filter-item-copy-btn"),
      onClick: () => {
        onCopyFilter(filter.definition);
        addToast({
          ...createBoldToastMessage("Shared filter copied", {
            entity: filter.definition?.name ?? ""
          }),
          status: TOAST_STATUS.SUCCESS
        });
      }
    });

    // Share (owner + collaborator)
    if (isOwner || isCollaborator) {
      actions.push({
        iconName: "share-nodes" as const,
        tooltipProps: { message: appstr("Share filter") },
        dataTestId: chartModeTestId("filter", "shared-filter-item-share-btn"),
        onClick: () => setShareModalDoc(filter)
      });
    }

    // Edit (owner + collaborator)
    if ((isOwner || isCollaborator) && onEditSharedFilter) {
      actions.push({
        iconName: "pen-to-square" as const,
        tooltipProps: { message: appstr("Edit filter") },
        dataTestId: chartModeTestId("filter", "shared-filter-item-edit-btn"),
        onClick: () => {
          onOpenEdit?.();
          onEditSharedFilter(filter);
        }
      });
    }

    return actions;
  };

  return (
    <Flex direction="column" height="100%">
      <Flex direction="column" padding="16px">
        <Text size="small">{appstr("Shared filters info")}</Text>
      </Flex>
      {loading ? (
        <Flex direction="column" padding="32px 16px" alignItems="center">
          <Loader.Spinner />
        </Flex>
      ) : nonOwnerFilters.length === 0 ? (
        <BigInfoIcon color="brand" name="filter" message={appstr("No shared filters yet")} />
      ) : (
        <>
          <Flex className="sidebar-search-row" gap="8px" alignItems="center" padding="0 16px 8px">
            <Input
              placeholder={appstr("Search filters")}
              value={searchTerm}
              onInput={(e: any) => setSearchTerm(e.target.value)}
              size="small"
              dataTestId="shared-filters-search"
            />
            <Button
              iconOnly
              size="small"
              modifier="tertiary"
              iconProps={{ name: "arrow-down-a-z" }}
              tooltipProps={{ message: appstr("Sort") }}
              onClick={() => setSortAsc(prev => !prev)}
              dataTestId={chartModeTestId("filter", "shared-filters-sort-toggle")}
            />
          </Flex>
          <Flex
            className="ocn-list-scrollbar"
            flex="1"
            direction="column"
            overflow="auto"
            padding="0 16px 16px"
          >
            {filteredSorted.map((filter: SharedFilterDoc) => (
              <Flex key={filter._id} className="ocn-filter-block-list-item">
                <ActionListItem
                  padding={"0px"}
                  dataTestId={`shared-filter-item--${filter._id}`}
                  actions={getActions(filter)}
                  label={filter.definition?.name || "Untitled"}
                  labelProps={{
                    size: "small",
                    modifier: enabledIds.has(filter._id) ? "bold" : undefined
                  }}
                  contentBefore={
                    <Checkbox
                      id={`shared-filter-enable-${filter._id}`}
                      checked={enabledIds.has(filter._id)}
                      size="medium"
                      onToggle={() => handleToggle(filter)}
                      data-testid={chartModeTestId("filter", "shared-filter-item-checkbox")}
                    />
                  }
                />
              </Flex>
            ))}
          </Flex>
        </>
      )}
      {shareModalDoc &&
        (() => {
          const sharedBlock = new QFilterBlock();
          sharedBlock.LoadFromJSON(shareModalDoc.definition);
          return (
            <ShareFilterModal
              show={true}
              onClose={() => setShareModalDoc(null)}
              filterType="filter"
              definition={shareModalDoc.definition}
              filterName={shareModalDoc.definition?.name || ""}
              filterRef={sharedBlock}
              onShareComplete={() => fetchSharedItems("filter")}
              editMode={true}
              existingFilterId={shareModalDoc._id}
              existingSharedWith={shareModalDoc.sharedWith}
            />
          );
        })()}
    </Flex>
  );
};
